import { createFileRoute, Link, Outlet, useRouterState } from '@tanstack/react-router'
import {
  CircleCheckIcon,
  CircleSlashIcon,
  CircleXIcon,
  HourglassIcon,
  InboxIcon,
  ListTodoIcon,
  MessagesSquareIcon,
  SlidersHorizontalIcon,
  StampIcon,
} from 'lucide-react'
import { Fragment } from 'react'
import { formatCount } from '@/lib/format'
import { useOutreachStats } from '@/lib/outreach'
import { cn } from '@/lib/utils'
import { useSiteScope } from '../-section'

/**
 * The drafts, the threads they became and how those ended, and the machine's
 * work either side of them. A
 * sidebar rather than tabs: the tab row is which section you are in, and these
 * are one section asked several times.
 *
 * The layout is pathless so a single prospect stays out of it.
 */
export const Route = createFileRoute('/_app/$org/$host/outreach/_list')({
  component: OutreachLayout,
})

const SECTIONS = [
  { to: '/$org/$host/outreach', key: 'outreach', label: 'Drafts', icon: InboxIcon },
  { to: '/$org/$host/outreach/approved', key: 'approved', label: 'Approved', icon: StampIcon },
  { to: '/$org/$host/outreach/active', key: 'active', label: 'Active', icon: MessagesSquareIcon },
  {
    to: '/$org/$host/outreach/accepted',
    key: 'accepted',
    label: 'Accepted',
    icon: CircleCheckIcon,
    heading: 'Resolved',
  },
  { to: '/$org/$host/outreach/rejected', key: 'rejected', label: 'Rejected', icon: CircleXIcon },
  { to: '/$org/$host/outreach/no-reply', key: 'no-reply', label: 'No reply', icon: HourglassIcon },
  { to: '/$org/$host/outreach/pipeline', key: 'pipeline', label: 'Pipeline', icon: ListTodoIcon },
  {
    to: '/$org/$host/outreach/dismissed',
    key: 'dismissed',
    label: 'Dismissed',
    icon: CircleSlashIcon,
  },
  {
    to: '/$org/$host/outreach/settings',
    key: 'settings',
    label: 'Settings',
    icon: SlidersHorizontalIcon,
  },
] as const

function OutreachLayout() {
  const { org, host, site } = useSiteScope()
  const { data } = useOutreachStats(site)
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const current = pathname.replace(/\/$/, '').split('/').at(-1)

  const stats = data?.stats
  const size: Record<(typeof SECTIONS)[number]['key'], number | undefined> = {
    outreach: stats?.messages.draft,
    approved: stats?.messages.approved,
    active: stats ? stats.prospects.contacted + stats.prospects.replied : undefined,
    accepted: stats?.outcomes.accepted,
    rejected: stats?.outcomes.rejected,
    'no-reply': stats?.outcomes['no-reply'],
    pipeline: stats ? stats.prospects.candidate + stats.prospects.researching : undefined,
    dismissed: stats?.prospects.dismissed,
    settings: undefined,
  }

  return (
    <div className="flex flex-col gap-8 sm:flex-row">
      <nav className="flex flex-wrap gap-1 sm:w-44 sm:shrink-0 sm:flex-col sm:flex-nowrap">
        {SECTIONS.map((section) => {
          const active = current === section.key
          const count = size[section.key]
          const heading = 'heading' in section ? section.heading : undefined
          return (
            <Fragment key={section.key}>
              {heading && (
                <span className="mono-label mt-4 hidden px-2.5 pb-1 text-muted-foreground sm:block">
                  {heading}
                </span>
              )}
              <Link
                to={section.to}
                params={{ org, host }}
                className={cn(
                  'flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] transition-colors',
                  active
                    ? 'bg-plum-selected text-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                  (section.key === 'pipeline' || section.key === 'settings') && 'mt-4',
                )}
              >
                <section.icon className={cn('size-3.5', active ? 'text-plum' : 'opacity-70')} />
                <span className="min-w-0 truncate">{section.label}</span>
                {data && count !== undefined && (
                  <span className="ml-auto pl-2 font-mono text-[11px] text-muted-foreground tabular">
                    {formatCount(count)}
                  </span>
                )}
              </Link>
            </Fragment>
          )
        })}
      </nav>

      <div className="min-w-0 flex-1 space-y-4">
        <Outlet />
      </div>
    </div>
  )
}
